import { devLog } from '../utils/devLogger';
// Google Meet / Calendar integration service
// Uses Google Calendar REST API v3 with an OAuth 2.0 access token.

const CALENDAR_API = 'https://www.googleapis.com/calendar/v3';
const MIN_ATTENDANCE_MINUTES = 15;

class GoogleMeetService {
  constructor() {
    this.accessToken = null;
  }

  initializeClient(accessToken) {
    this.accessToken = accessToken;
    devLog('✅ Google Meet service initialised');
    return { success: true };
  }

  clearClient() {
    this.accessToken = null;
  }

  _headers() {
    return {
      Authorization: `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
    };
  }

  async _get(path) {
    if (!this.accessToken) return { success: false, error: 'Not authenticated' };
    try {
      const res = await fetch(`${CALENDAR_API}${path}`, { headers: this._headers() });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return { success: true, data: await res.json() };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }

  async _post(path, body) {
    if (!this.accessToken) return { success: false, error: 'Not authenticated' };
    try {
      const res = await fetch(`${CALENDAR_API}${path}`, {
        method: 'POST',
        headers: this._headers(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return { success: true, data: await res.json() };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }

  // Creates a Google Calendar event with an auto-generated Meet link.
  // Returns { success, data: { meetLink, eventId, ... } }
  async createMeeting(sessionData) {
    const {
      title,
      description = '',
      startDateTime,
      endDateTime,
      attendeeEmails = [],
    } = sessionData;

    const event = {
      summary: title,
      description,
      start: { dateTime: new Date(startDateTime).toISOString() },
      end: { dateTime: new Date(endDateTime).toISOString() },
      attendees: attendeeEmails.map((email) => ({ email })),
      conferenceData: {
        createRequest: {
          requestId: `npi-lms-${Date.now()}`,
          conferenceSolutionKey: { type: 'hangoutsMeet' },
        },
      },
    };

    const result = await this._post(
      '/calendars/primary/events?conferenceDataVersion=1&sendUpdates=none',
      event
    );

    if (!result.success) return result;

    const eventData = result.data;
    return {
      success: true,
      data: {
        eventId: eventData.id,
        meetLink: eventData.hangoutLink || eventData.conferenceData?.entryPoints?.[0]?.uri || '',
        htmlLink: eventData.htmlLink,
        title: eventData.summary,
        startTime: eventData.start?.dateTime,
        endTime: eventData.end?.dateTime,
      },
    };
  }

  // Fetches calendar events that have a Meet link within [startTime, endTime].
  async getUserMeetings(startTime, endTime) {
    const params = new URLSearchParams({
      timeMin: new Date(startTime).toISOString(),
      timeMax: new Date(endTime).toISOString(),
      singleEvents: 'true',
      orderBy: 'startTime',
      maxResults: '50',
    });

    const result = await this._get(`/calendars/primary/events?${params}`);
    if (!result.success) return result;

    const meetings = (result.data.items || [])
      .filter((e) => e.hangoutLink || e.conferenceData?.entryPoints?.length)
      .map((e) => ({
        id: e.id,
        subject: e.summary,
        meetLink: e.hangoutLink || e.conferenceData?.entryPoints?.[0]?.uri || '',
        startTime: e.start?.dateTime,
        endTime: e.end?.dateTime,
        htmlLink: e.htmlLink,
      }));

    return { success: true, data: meetings };
  }

  // Parses Google Meet attendance CSV into a standard shape.
  // Google Meet CSV columns: "Name", "Email Address", "Duration", "Joined", "Left"
  parseMeetAttendanceCsv(csvText) {
    try {
      const lines = csvText.trim().split('\n');
      if (lines.length < 2) return { success: false, error: 'CSV has no data rows' };

      const header = lines[0].split(',').map((h) => h.trim().replace(/"/g, '').toLowerCase());
      const nameIdx = header.findIndex((h) => h.includes('name'));
      const emailIdx = header.findIndex((h) => h.includes('email'));
      const durationIdx = header.findIndex((h) => h.includes('duration'));
      const joinIdx = header.findIndex((h) => h.includes('join'));
      const leaveIdx = header.findIndex((h) => h.includes('left') || h.includes('leave'));

      const records = lines.slice(1)
        .filter((l) => l.trim())
        .map((line) => {
          const cols = line.split(',').map((c) => c.trim().replace(/"/g, ''));
          const durationRaw = durationIdx >= 0 ? cols[durationIdx] : '0';
          const durationMinutes = parseDurationMinutes(durationRaw);
          return {
            displayName: nameIdx >= 0 ? cols[nameIdx] : '',
            email: emailIdx >= 0 ? cols[emailIdx] : '',
            durationMinutes,
            totalAttendanceInSeconds: durationMinutes * 60,
            joinTime: joinIdx >= 0 ? cols[joinIdx] : null,
            leaveTime: leaveIdx >= 0 ? cols[leaveIdx] : null,
          };
        });

      return { success: true, data: records };
    } catch (err) {
      return { success: false, error: `Failed to parse CSV: ${err.message}` };
    }
  }

  // Reconciles manual LMS attendance against Meet CSV records.
  // manualAttendance: [{ learnerId, name, email, date, status }]
  // meetRecords: parsed CSV rows from parseMeetAttendanceCsv
  reconcileAttendance(manualAttendance, meetRecords, attendanceThresholdMinutes = MIN_ATTENDANCE_MINUTES) {
    try {
      const meetByEmail = new Map(
        meetRecords
          .filter((r) => r.email)
          .map((r) => [r.email.toLowerCase(), r])
      );
      const meetByName = new Map(
        meetRecords.map((r) => [r.displayName.toLowerCase(), r])
      );

      const reconciled = manualAttendance.map((learner) => {
        const email = (learner.email || '').toLowerCase();
        const name = (learner.name || learner.displayName || '').toLowerCase();
        const meetRecord = meetByEmail.get(email) || meetByName.get(name) || null;

        const durationMinutes = meetRecord?.durationMinutes || 0;
        const meetPresent = durationMinutes >= attendanceThresholdMinutes;

        let finalStatus = learner.status;
        const discrepancy = (learner.status === 'present') !== meetPresent;

        if (discrepancy && meetPresent) finalStatus = 'present';
        if (discrepancy && !meetPresent && learner.status === 'present') finalStatus = 'absent';

        return {
          learnerId: learner.learnerId,
          learnerName: learner.name || learner.displayName || '',
          email: learner.email || '',
          manualStatus: learner.status,
          meetPresent,
          meetDurationMinutes: durationMinutes,
          finalStatus,
          discrepancy,
        };
      });

      return { success: true, data: reconciled };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }
}

function parseDurationMinutes(raw) {
  if (!raw) return 0;
  // "1:23:45" (h:m:s) or "45:12" (m:s) or plain number (minutes)
  const parts = raw.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 60 + parts[1];
  if (parts.length === 2) return parts[0];
  return Number(raw) || 0;
}

const googleMeetService = new GoogleMeetService();
export default googleMeetService;
